"use client";

import { useEffect, useState } from "react";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";

/**
 * 다매장 selector (사용자 결정 2026-05-09).
 *
 * 매장 2개 이상 보유 시 홈 매장 카드 위에 노출 — 활성 매장 변경.
 * 매장 1개 = 노출 X (불필요).
 * 비로그인 / 매장 미등록 = 노출 X.
 *
 * activeStoreId 변경 → StoreMiniView / GapAnalysisCard / NeighborhoodCard 모두 자동 갱신.
 */

interface Store {
  id: number;
  name: string;
}

interface StoreListOut {
  items: Store[];
}

interface Props {
  /** true 시 외곽 section 래퍼 제거 — 부모가 padding 제어 (지도 페이지 등) */
  inline?: boolean;
}

export function ActiveStoreSelector({ inline = false }: Props = {}) {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const { activeStoreId, setActiveStoreId } = useActiveStore();

  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    if (!hasHydrated || !isLoggedIn || !token) {
      setStores([]);
      return;
    }
    let cancelled = false;
    axios
      .get<StoreListOut>("/api/stores", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        if (!cancelled) setStores(r.data.items || []);
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => { cancelled = true; };
  }, [hasHydrated, isLoggedIn, token]);

  // 매장 1개 이하면 셀렉터 자체 노출 X
  if (!hasHydrated || !isLoggedIn || stores.length < 2) return null;

  // 사용자 결정 2026-05-13 — 강조: 본문 카드 배경과 구분되는 primary tint + 라벨 강화 + 화살표 명확
  const inner = (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-primary/10 border-2 border-primary/40 shadow-sm">
      <span className="text-2xs font-bold text-primary shrink-0 flex items-center gap-1">
        활성 매장
      </span>
      <div className="relative flex-1">
        <select
          value={activeStoreId ?? ""}
          onChange={(e) => setActiveStoreId(Number(e.target.value))}
          className="w-full text-sm font-bold text-text bg-transparent border-0 focus:outline-none cursor-pointer appearance-none pr-6"
        >
          {stores.map((s) => (
            // 5/11 #8 fix — PC dark mode native select option 흰바탕+흰글씨 issue.
            <option
              key={s.id}
              value={s.id}
              style={{ background: "#1a1a1a", color: "#fff" }}
            >
              {s.name}
            </option>
          ))}
        </select>
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-primary text-xs pointer-events-none">▼</span>
      </div>
      <span className="text-2xs text-text-sub shrink-0">
        ({stores.length}개)
      </span>
    </div>
  );

  if (inline) return inner;
  return <section className="px-4 mt-4">{inner}</section>;
}
